import {
  Dialog,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/Dialog"
import { Button } from "@/components/ui/Button"
import { Unplug, CheckCircle, Plus } from "lucide-react"
import { cn } from "@/utils/Functions"
import type { NylasConnection } from "@equaliq/api-model-v2"
import type { Integration } from "@/types/integrations.types"

interface ManageConnectionsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  integration: Integration
  connections: NylasConnection[]
  onDisconnect: (connectionId: string) => void
  onAddAnother: () => void
  disconnecting?: boolean
  connecting?: boolean
}

export function ManageConnectionsDialog({
  open,
  onOpenChange,
  integration,
  connections,
  onDisconnect,
  onAddAnother,
  disconnecting = false,
  connecting = false,
}: ManageConnectionsDialogProps) {
  const Icon = integration.icon

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup>
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div
              className={cn(
                "flex items-center justify-center size-10 rounded-xl bg-gradient-to-br border",
                integration.accentFrom,
                integration.accentTo,
                integration.accentBorder
              )}
            >
              <Icon className="size-5 text-foreground/80" />
            </div>
            <DialogTitle>Manage {integration.name}</DialogTitle>
          </div>
          <DialogDescription>
            {connections.length === 1
              ? "You have 1 connected account"
              : `You have ${connections.length} connected accounts`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 mt-4">
          {connections.map((conn) => (
            <div
              key={conn.connectionId}
              className="flex items-center justify-between p-4 rounded-lg border border-border bg-card/50"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium text-foreground truncate">
                    {conn.email}
                  </p>
                  {conn.enabled && (
                    <CheckCircle className="size-4 text-green-400 shrink-0" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Provider: {conn.provider} • Connected{" "}
                  {new Date(conn.connectedAt).toLocaleDateString()}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="ml-4 gap-2 text-destructive hover:bg-destructive/10 shrink-0"
                onClick={() => onDisconnect(conn.connectionId)}
                disabled={disconnecting}
              >
                <Unplug className="size-3.5" />
                Disconnect
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2"
            onClick={onAddAnother}
            disabled={connecting}
          >
            <Plus className="size-4" />
            Add another account
          </Button>
        </div>
      </DialogPopup>
    </Dialog>
  )
}
